// Turns any API failure into one readable sentence for the UI.
//
// The backend answers in two shapes: its own ApiResponse
// ({ message, errors: [..] }) and ASP.NET model validation
// ({ title, errors: { Field: ['..'] } }). Both are handled here.

const firstValidationMessage = errors => {
  if (!errors) return null;
  if (Array.isArray(errors)) return errors.find(Boolean) || null;
  if (typeof errors === 'object') {
    for (const value of Object.values(errors)) {
      if (Array.isArray(value) && value.length) return value[0];
      if (typeof value === 'string' && value) return value;
    }
  }
  return null;
};

const byStatus = {
  400: 'Some details are missing or invalid. Please check and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: "You don't have permission to do that.",
  404: "We couldn't find what you were looking for.",
  409: 'This already exists.',
  413: 'That file is too large.',
  429: 'Too many attempts. Please wait a minute and try again.',
  500: 'Something went wrong on our side. Please try again.',
  502: 'The service is temporarily unavailable. Please try again shortly.',
  503: 'The service is temporarily unavailable. Please try again shortly.',
  504: 'The server took too long to respond. Please try again.',
};

export const getErrorMessage = (error, fallback) => {
  const response = error?.response;
  const data = response?.data;

  if (data && typeof data === 'object') {
    const validation = firstValidationMessage(data.errors);
    // Generic wrappers like "Validation failed" are less useful than the field message.
    if (validation && (!data.message || /validation failed/i.test(data.message))) {
      return validation;
    }
    if (data.message) return data.message;
    if (validation) return validation;
  }
  if (typeof data === 'string' && data.length < 200) return data;

  if (response?.status && byStatus[response.status]) return byStatus[response.status];
  if (error?.request && !response) {
    return "Can't reach the server. Check your connection and try again.";
  }
  return fallback || error?.message || 'Something went wrong. Please try again.';
};

export default getErrorMessage;
